import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DocumentTranscriptTextV1,
  DocumentTranscriptUtteranceV1,
  ScriptixDescendant
} from '@utils/types/editors/transcripts/transcript.ts';
import { createEditor, Descendant, NodeEntry, Text } from 'slate';
import { Editable, RenderElementProps, Slate, withReact } from 'slate-react';
import { HistoryEditor, withHistory } from 'slate-history';
import { RenderLeafProps } from 'slate-react/dist/components/editable';
import { useSharedEditorContext } from '@utils/providers/shared-editor-context.tsx';
import { useSharedTranscript } from '@utils/providers/shared-transcript-context.tsx';
import { SharedTranscriptEditorLevelHotkeyProvider } from '@utils/providers/shared-transcript-hotkey-editor-context.tsx';
import { SharedParagraphElement } from '@components/editors/shared-editor/shared-transcript/shared-paragraph-element.tsx';
import { SharedParagraphLeaf } from '@utils/api/magic-links/shared-paragraph-leaf.tsx';
import {
  createSafeSlateClickGuard,
  isAtStartOfNode,
  withErrorHandling
} from '@utils/extensions/slate-editor-utils.ts';

type Props = {
  index: number;
  paragraph: DocumentTranscriptUtteranceV1;
  onWordOutOfView: (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => void;
  onWordInView: () => void;
};

const SharedScriptixEditor = ({
  index,
  paragraph,
  onWordOutOfView,
  onWordInView
}: Props) => {
  const {
    search,
    caseSensitive,
    wholeWord,
    currentMatch,
    document,
    setEditor,
    editorUpdateParagraph
  } = useSharedTranscript();
  const { addEditor, removeEditor } = useSharedEditorContext();
  const [editor] = useState(() =>
    withErrorHandling(withHistory(withReact(createEditor())))
  );

  useEffect(() => {
    // Register the editor
    addEditor(index.toString(), editor);

    return () => {
      // Unregister the editor
      removeEditor(index.toString());
    };
  }, [index, editor]);

  const initialValue = useMemo(() => {
    if (!paragraph || ('children' in paragraph && !paragraph['children']))
      return [];

    const initialValues: ScriptixDescendant[] = [];
    initialValues.push({
      ...paragraph,
      id: index,
      type: 'speaker',
      children: [
        {
          text: paragraph.speaker || ''
        }
      ]
    } as ScriptixDescendant);

    initialValues.push({
      ...paragraph,
      id: index,
      type: 'paragraph',
      children: paragraph.children
    } as ScriptixDescendant);

    return initialValues;
  }, [document]);

  // Decorate function for highlighting search matches
  const decorate = useCallback(
    (entry: NodeEntry) => {
      const [node, path] = entry;
      const ranges = [];

      if (search && Text.isText(node)) {
        const { text } = node;
        const flags = caseSensitive ? 'g' : 'gi';
        const searchQuery = wholeWord ? `\\b${search}\\b` : search;
        const regex = new RegExp(searchQuery, flags);
        let match;

        while ((match = regex.exec(text)) !== null) {
          const start = match.index;
          const end = start + match[0].length;
          const isCurrent = currentMatch
            ? currentMatch.start === (node as DocumentTranscriptTextV1).start &&
              currentMatch.stop === (node as DocumentTranscriptTextV1).stop
            : false;
          ranges.push({
            anchor: { path, offset: start },
            focus: { path, offset: end },
            highlight: true,
            isCurrentMatch: isCurrent
          });
        }
      }

      return ranges;
    },
    [search, caseSensitive, wholeWord, currentMatch]
  );

  const handleChange = (children: Descendant[]) => {
    editorUpdateParagraph(children);
  };

  const renderElement = useCallback((props: RenderElementProps) => {
    return (
      <SharedParagraphElement
        index={index}
        {...props}
        onWordOutOfView={onWordOutOfView}
        onWordInView={onWordInView}
        editor={editor}
      />
    );
  }, []);

  const renderLeaf = useCallback((props: RenderLeafProps) => {
    return <SharedParagraphLeaf {...props} />;
  }, []);

  return (
    <SharedTranscriptEditorLevelHotkeyProvider editor={editor}>
      <Slate
        editor={editor}
        initialValue={initialValue}
        onChange={handleChange}
      >
        <Editable
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          // Do NOT autoFocus: Virtuoso mounts many rows synchronously, and every
          // Editable's autoFocus fires on mount — the last one wins and the
          // browser scrolls that contenteditable into view, jumping the editor
          // to the end of the visible list on open.
          onDrop={e => e.preventDefault()}
          onClick={createSafeSlateClickGuard(editor)}
          style={{
            outline: 'unset',
            padding: '0em 0.5em',
            border: 'unset',
            width: '100%'
          }}
          className="no-outline"
          onKeyDown={event => {
            if (event.ctrlKey) {
              switch (event.key) {
                case 'y':
                  event.preventDefault();
                  HistoryEditor.redo(editor as unknown as HistoryEditor);
                  break;
                // Ctrl/Cmd +, = and - stay with the browser (page zoom).
                case 'f':
                case 'left':
                case 'right':
                case 'b':
                case 'i':
                case 'u':
                case 's':
                  event.preventDefault();
                  break;
              }
            } else if (event.key === 'ArrowLeft' || event.key === 'Backspace') {
              // If we're at the start of a node, prevent moving left due to moving to speaker crashing error
              if (isAtStartOfNode(editor)) {
                event.preventDefault();
              }
            }
          }}
          decorate={decorate}
          onFocus={() => setEditor(editor)}
        />
      </Slate>
    </SharedTranscriptEditorLevelHotkeyProvider>
  );
};

export default SharedScriptixEditor;
